import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { m, useReducedMotion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BookOpenCheck,
  Check,
  CircleDot,
  IndianRupee,
  Pause,
  Play,
  School,
  Sparkles,
  UserPlus,
} from "lucide-react";
import { cn } from "../lib/utils";
import {
  attachAvifSource,
  productStoryAvifSrcSetFor,
  productStoryCropFor,
  productStorySrcSetFor,
} from "../pages/landingparts/productStoryContent";
import { useMarketingMotion } from "./marketing/MarketingMotion";

/*
 * A code-controlled product film built from the governed product-story media.
 * The images are illustrative marketing previews with synthetic sample data—not
 * live customer screens. Keeping the disclosure inside this figure prevents the
 * hero animation from being mistaken for product or customer evidence.
 */

export const HERO_STORY_INTERVAL_MS = 3000;
export const HERO_STORY_AUTO_MEDIA_QUERY = "(min-width: 1024px) and (hover: hover) and (pointer: fine)";
// ONE sizes vocabulary shared by the visible scene <img> and warmScene's preloader, so the
// preload warms EXACTLY the srcset candidate the browser will render (PERF-5/VIS-2) — never the
// ~1MB native master on a viewport that renders a downscaled rung.
//
// PERF-9: MEASURED from the real hero layout instead of estimated. HeroScrollReveal wraps this in
// `px-4` + `w-[min(1120px,94vw)]` on mobile and `w-[min(1220px,93vw)]` on desktop; the dashboard
// shell adds `px-4 sm:px-5`, and at lg the frame becomes the 1fr side of
// `lg:grid-cols-[minmax(11.5rem,0.32fr)_minmax(0,1fr)] lg:gap-5` (≈0.7576 of the free space),
// then `xl:w-[84%]`:
//   <640px   : 94vw − 32px
//   640-1023 : 94vw − 40px
//   1024-1279: ≈0.7576 × (93vw − 60px)          → ~70vw − 45px
//   ≥1280px  : the wrapper caps at 1220px, so the frame settles at a FIXED ~738px
// The old string declared 100vw on phones (a 390px phone fetched w960 for a ~335px frame) and
// UNDER-declared 42vw on desktop.
export const HERO_IMAGE_SIZES =
  "(min-width: 1280px) 738px, (min-width: 1024px) calc(70vw - 45px), (min-width: 640px) calc(94vw - 40px), calc(94vw - 32px)";
// The floating detail cards: ~19vw of the hero frame at xl (measured 270px at 1440 — the earlier
// 14vw under-declared by a third, so a DPR-2 display fetched a tier covering only ~97% of the
// drawn pixels; caught by responsive-story-media's density gate), ~45vw at sm, and a single
// gutter-inset column on phones. Shared by the visible <img> and its AVIF <source>.
const HERO_CROP_SIZES = "(min-width: 1280px) 19vw, (min-width: 640px) 45vw, calc(100vw - 48px)";
const EASE_OUT = [0.23, 1, 0.32, 1];

function shouldAutoPlayByDefault() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(HERO_STORY_AUTO_MEDIA_QUERY).matches;
}

const fragment = (id, label, crop) => Object.freeze({ id, label, crop: Object.freeze(crop) });

export const HERO_PRODUCT_SCENES = Object.freeze([
  Object.freeze({
    id: "unified-erp",
    label: "Unified ERP",
    eyebrow: "One connected school",
    title: "See the whole school day in one operating view.",
    problem: "Separate tools force teams to re-enter context and reconcile competing versions of the same record.",
    outcome: "Academics, finance, people, transport and assigned work stay visible in one shared operating context.",
    image: "/marketing/product-story/v2/01-unified-erp.webp",
    imageWidth: 1586,
    imageHeight: 992,
    imageAlt: "Illustrative executive view connecting academics, finance, staff and school operations.",
    icon: School,
    accent: "--vp-accent-active",
    fragments: Object.freeze([
      fragment("daily-workflow", "Connected daily workflow", { x: 12.1, y: 17.4, w: 68.9, h: 10.5 }),
      fragment("assigned-work", "Human-assigned work queue", { x: 82, y: 7.3, w: 17.7, h: 42 }),
      fragment("task-handoffs", "Cross-team task hand-offs", { x: 12.3, y: 82.2, w: 68.6, h: 14.1 }),
    ]),
    availability: "Core platform with plan- and rollout-controlled extensions",
  }),
  Object.freeze({
    id: "academics",
    label: "Academics",
    eyebrow: "From plan to result",
    title: "Keep every learning record connected.",
    problem: "Curriculum, timetable, attendance, assignments and results lose meaning when each is maintained in isolation.",
    outcome: "Authorised teams can plan classes, record attendance and publish approved results from connected academic records.",
    image: "/marketing/product-story/v2/02-academics.webp",
    imageWidth: 1536,
    imageHeight: 1024,
    imageAlt: "Illustrative academic view with timetable, attendance, assignments and curriculum progress.",
    icon: BookOpenCheck,
    accent: "--vp-accent-active",
    fragments: Object.freeze([
      fragment("curriculum-progress", "Curriculum progress", { x: 61.6, y: 11.5, w: 35.5, h: 43 }),
      fragment("attendance-register", "Attendance register", { x: 12.8, y: 56.6, w: 19.6, h: 27.8 }),
      fragment("report-card-approval", "Report-card approvals", { x: 74.5, y: 56.5, w: 22.8, h: 27.8 }),
    ]),
    availability: "Core academic workflows with selected plan-gated extensions",
  }),
  Object.freeze({
    id: "fee-finance",
    label: "Fee & finance",
    eyebrow: "Invoice-led finance",
    title: "Make every fee state reviewable.",
    problem: "Disconnected invoices, payment evidence and concessions leave finance teams reconciling the same transaction more than once.",
    outcome: "Invoices, provider evidence, dues, concessions, expenses and supported reports remain connected without confusing evidence with settlement.",
    image: "/marketing/product-story/v2/03-fee-finance.webp",
    imageWidth: 1568,
    imageHeight: 1003,
    imageAlt: "Illustrative fee and finance view with invoices, collection evidence, dues and reports.",
    icon: IndianRupee,
    accent: "--vp-orange-text",
    fragments: Object.freeze([
      fragment("provider-evidence", "Payment provider evidence", { x: 12.6, y: 25.4, w: 44.9, h: 25.8 }),
      fragment("overdue-review", "Overdue review queue", { x: 58.1, y: 25.4, w: 40.6, h: 25.8 }),
      fragment("reconciliation-boundary", "Clear reconciliation boundary", { x: 12.6, y: 75.8, w: 44.8, h: 20.7 }),
    ]),
    availability: "Provider, plan and approval requirements vary by workflow",
  }),
  Object.freeze({
    id: "admissions",
    label: "Admissions",
    eyebrow: "Enquiry to student record",
    title: "Move each family through a controlled hand-off.",
    problem: "Enquiries are easy to lose when follow-up, documents, decisions and student creation happen in separate places.",
    outcome: "A six-stage pipeline keeps ownership, document review and authorised student conversion visible to the right team.",
    image: "/marketing/product-story/v2/05-admissions.webp",
    imageWidth: 1586,
    imageHeight: 992,
    imageAlt: "Illustrative admissions view showing enquiry, document review and enrolment stages.",
    icon: UserPlus,
    accent: "--vp-success",
    fragments: Object.freeze([
      fragment("admissions-pipeline", "Six-stage admissions pipeline", { x: 7.6, y: 24.8, w: 64.3, h: 42 }),
      fragment("document-verification", "Document verification", { x: 73, y: 22.1, w: 25.7, h: 40.2 }),
      fragment("conversion-confirmation", "Authorised student conversion", { x: 73, y: 63.3, w: 25.7, h: 20.4 }),
    ]),
    availability: "Core CRM workflow; public form and OTP setup may be required",
  }),
  Object.freeze({
    id: "aira-ai",
    label: "Aira AI",
    eyebrow: "Governed assistance",
    title: "Ask across school records without surrendering control.",
    problem: "Leaders need answers across operating records without giving an assistant unrestricted access or autonomous control.",
    outcome: "Supported questions stay permission-scoped, answers show their sources and proposed actions wait for a person to confirm.",
    image: "/marketing/product-story/v2/06-aira-ai.webp",
    imageWidth: 1568,
    imageHeight: 1003,
    imageAlt: "Illustrative governed Aira workspace with source-backed answers and a human confirmation gate.",
    icon: Sparkles,
    accent: "--vp-ai-ink",
    fragments: Object.freeze([
      fragment("permission-boundary", "Permission boundary", { x: 7.2, y: 6.2, w: 49, h: 11.4 }),
      fragment("grounded-sources", "Source-backed answer", { x: 12.8, y: 31, w: 35.3, h: 41.5 }),
      fragment("confirmation-audit", "Confirmation gate and audit trail", { x: 57.8, y: 52, w: 40.9, h: 44 }),
    ]),
    availability: "Capabilities depend on entitlement, supported workflow and provider setup",
  }),
]);

// Kept as an export alias because earlier tests and internal consumers used this name.
export const SCHOOL_DAY_STORIES = HERO_PRODUCT_SCENES;

function ExactProductCrop({ scene, fragment: cropFragment, loading = "lazy" }) {
  const { crop } = cropFragment;
  const cropMedia = productStoryCropFor(scene, cropFragment);

  return (
    <div
      className="relative w-full overflow-hidden bg-surface"
      style={{ aspectRatio: cropMedia.width / cropMedia.height }}
      data-crop={`${crop.x},${crop.y},${crop.w},${crop.h}`}
      data-crop-id={cropFragment.id}
    >
      <picture className="contents">
        <source type="image/avif" srcSet={cropMedia.avifSrcSet} sizes={HERO_CROP_SIZES} />
        <img
          src={cropMedia.image}
          srcSet={cropMedia.srcSet}
          sizes={HERO_CROP_SIZES}
          alt=""
          aria-hidden="true"
          width={cropMedia.width}
          height={cropMedia.height}
          loading={loading}
          decoding="async"
          draggable="false"
          data-image-treatment="sharp-unfiltered-crop"
          className="pointer-events-none block h-auto w-full select-none object-contain [filter:none] [image-rendering:auto] [transform:none] transition-none"
        />
      </picture>
    </div>
  );
}

function ProductFragment({ scene, cropFragment, position, instant, direction = 1 }) {
  return (
    <m.figure
      data-floating="true"
      className={cn(
        "absolute z-20 hidden w-[31%] overflow-hidden rounded-xl border border-line/80 bg-canvas shadow-[0_18px_55px_-34px_rgba(2,12,35,0.72)] xl:block",
        position === "left" ? "-left-2 bottom-[5%]" : "-right-2 top-[4%]",
      )}
      initial={instant ? false : { opacity: 0, transform: `translate3d(${10 * direction}px,8px,0)` }}
      animate={{ opacity: 1, transform: "translate3d(0,0,0)" }}
      transition={{ duration: instant ? 0 : 0.24, delay: instant ? 0 : position === "left" ? 0.06 : 0.1, ease: EASE_OUT }}
    >
      <ExactProductCrop scene={scene} fragment={cropFragment} />
      <figcaption className="border-t border-line bg-canvas/95 px-3 py-2 text-[9px] font-semibold leading-snug text-ink-subtle">
        {cropFragment.label}
      </figcaption>
    </m.figure>
  );
}

function StoryTabs({ activeIndex, panelPrefix, onSelect, onKeyDown, onWarm, refs, running, clockToken }) {
  return (
    <div
      role="tablist"
      aria-label="Vidyapeeth360 product chapters"
      className="flex min-w-0 gap-1.5 overflow-x-auto px-3 py-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:px-5"
    >
      {HERO_PRODUCT_SCENES.map((scene, index) => {
        const selected = index === activeIndex;
        const Icon = scene.icon;
        return (
          <button
            key={scene.id}
            ref={(node) => { refs.current[index] = node; }}
            id={`${panelPrefix}-tab-${scene.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${panelPrefix}-panel-${scene.id}`}
            tabIndex={selected ? 0 : -1}
            onClick={(event) => onSelect(index, event.detail === 0 ? "keyboard" : "pointer")}
            onKeyDown={(event) => onKeyDown(event, index)}
            onPointerEnter={() => onWarm(index)}
            onFocus={() => onWarm(index)}
            data-testid={`hero-story-${scene.id}`}
            className={cn(
              "group relative flex min-h-11 min-w-[8.75rem] flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-left",
              "transition-[background-color,color] duration-150 ease-premium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/65",
              selected ? "bg-accent/10 text-ink" : "text-ink-subtle hover:bg-muted/70 hover:text-ink",
            )}
          >
            <Icon className={cn("h-3.5 w-3.5 shrink-0", selected ? "text-accent" : "text-ink-faint")} aria-hidden="true" />
            <span className="whitespace-nowrap text-[11px] font-semibold sm:text-xs">{scene.label}</span>
            <span className="absolute inset-x-2 bottom-0 h-px origin-left overflow-hidden bg-line" aria-hidden="true">
              <m.span
                key={`${scene.id}-${clockToken}-${running}`}
                data-testid={selected ? "hero-story-progress" : undefined}
                className="block h-full origin-left bg-accent"
                initial={{ transform: `scaleX(${index < activeIndex ? 1 : 0})` }}
                animate={{ transform: `scaleX(${index < activeIndex ? 1 : selected && running ? 1 : 0})` }}
                transition={{ duration: selected && running ? HERO_STORY_INTERVAL_MS / 1000 : 0, ease: "linear" }}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function StoryPanel({ scene, index, panelPrefix, liveMode, instant, onSwipe, direction = 1 }) {
  const Icon = scene.icon;
  const accent = `rgb(var(${scene.accent}) / 0.13)`;

  return (
    <m.div
      key={scene.id}
      id={`${panelPrefix}-panel-${scene.id}`}
      role="tabpanel"
      aria-labelledby={`${panelPrefix}-tab-${scene.id}`}
      aria-live={liveMode}
      data-testid="hero-story-panel"
      data-active-scene={scene.id}
      className="relative grid min-h-0 flex-1 gap-4 overflow-hidden px-4 pb-4 pt-3 sm:px-5 sm:pb-5 lg:grid-cols-[minmax(11.5rem,0.32fr)_minmax(0,1fr)] lg:items-center lg:gap-5"
      initial={instant ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: instant ? 0 : 0.24, ease: EASE_OUT }}
      onPointerDown={onSwipe.onPointerDown}
      onPointerUp={onSwipe.onPointerUp}
      onPointerCancel={onSwipe.onPointerCancel}
    >
      {/* Chapter-turn depth: the incoming chapter slides from its direction of travel, the text
          column travelling less far and settling sooner than the image frame — a parallax page
          turn rather than a flat crossfade. Keyboard + reduced-motion keep the instant path. */}
      <m.aside
        className="relative z-20 min-w-0 lg:self-center"
        initial={instant ? false : { opacity: 0, transform: `translate3d(${12 * direction}px,0,0)` }}
        animate={{ opacity: 1, transform: "translate3d(0,0,0)" }}
        transition={{ duration: instant ? 0 : 0.26, ease: EASE_OUT }}
      >
        <p className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-accent">
          <Icon className="h-3.5 w-3.5" aria-hidden="true" />
          {String(index + 1).padStart(2, "0")} · {scene.eyebrow}
        </p>
        <h3 className="mt-2 max-w-[20ch] font-heading text-[clamp(1.25rem,2vw,1.85rem)] font-bold leading-[1.08] tracking-[-0.035em] text-ink">
          {scene.title}
        </h3>
        <div className="mt-4 space-y-3">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-ink-subtle">Where work breaks</p>
            <p className="mt-1 text-[11.5px] leading-relaxed text-ink-subtle">{scene.problem}</p>
          </div>
          <div className="border-l-2 border-accent pl-3">
            <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-accent">Connected response</p>
            <p className="mt-1 text-[11.5px] font-medium leading-relaxed text-ink-2">{scene.outcome}</p>
          </div>
        </div>
      </m.aside>

      <div className="relative min-w-0 lg:self-center">
        <div
          className="pointer-events-none absolute -inset-10 opacity-80"
          style={{ background: `radial-gradient(circle at 52% 45%, ${accent}, transparent 64%)` }}
          aria-hidden="true"
        />
        <m.div
          className="relative z-10 mx-auto w-full xl:w-[84%]"
          initial={instant ? false : { opacity: 0, transform: `translate3d(${26 * direction}px,0,0) scale(1.012)` }}
          animate={{ opacity: 1, transform: "translate3d(0,0,0) scale(1)" }}
          transition={{ duration: instant ? 0 : 0.34, ease: EASE_OUT }}
        >
          <div
            className="overflow-hidden rounded-[13px] bg-surface shadow-[0_30px_75px_-45px_rgba(2,12,35,0.78)]"
            data-theme-stage="static-light"
          >
            <picture className="contents">
              {/* PERF-9: AVIF first; `contents` keeps <picture> boxless so layout is unchanged. */}
              <source
                type="image/avif"
                srcSet={productStoryAvifSrcSetFor(scene.image, scene.imageWidth)}
                sizes={HERO_IMAGE_SIZES}
              />
              <img
                src={scene.image}
                srcSet={productStorySrcSetFor(scene.image, scene.imageWidth)}
                sizes={HERO_IMAGE_SIZES}
                alt={scene.imageAlt}
                width={scene.imageWidth}
                height={scene.imageHeight}
                loading="eager"
                fetchPriority={index === 0 ? "high" : "auto"}
                decoding="async"
                draggable="false"
                data-testid="hero-product-image"
                data-image-treatment="sharp-unfiltered-full"
                className="block h-auto w-full select-none object-contain [filter:none] [image-rendering:auto] [transform:none] transition-none"
              />
            </picture>
          </div>
        </m.div>

        <ProductFragment scene={scene} cropFragment={scene.fragments[1]} position="right" instant={instant} direction={direction} />
        <ProductFragment scene={scene} cropFragment={scene.fragments[2]} position="left" instant={instant} direction={direction} />

        <figure
          className="relative z-20 mt-3 overflow-hidden rounded-xl border border-line bg-canvas shadow-card xl:hidden"
          data-testid="hero-mobile-detail"
        >
          <ExactProductCrop scene={scene} fragment={scene.fragments[0]} loading={index === 0 ? "eager" : "lazy"} />
          <figcaption className="border-t border-line px-3 py-2 text-[10px] font-semibold text-ink-subtle">
            {scene.fragments[0].label}
          </figcaption>
        </figure>
      </div>
    </m.div>
  );
}

export default function HeroDashboard({ active = false }) {
  const prefersReducedMotion = useReducedMotion();
  const { paused } = useMarketingMotion();
  const motionBlocked = Boolean(prefersReducedMotion || paused);
  const [activeIndex, setActiveIndex] = useState(0);
  const [autoPlayRequested, setAutoPlayRequested] = useState(shouldAutoPlayByDefault);
  const [focusPauseLocked, setFocusPauseLocked] = useState(false);
  const [documentVisible, setDocumentVisible] = useState(() => (
    typeof document === "undefined" || document.visibilityState !== "hidden"
  ));
  const [inputSource, setInputSource] = useState("autoplay");
  const [travelDirection, setTravelDirection] = useState(1);
  const [clockToken, setClockToken] = useState(0);
  const tabRefs = useRef([]);
  const swipeStart = useRef(null);
  const rotationControlPointerDown = useRef(false);
  const autoPlayTimerRef = useRef(null);
  const scenePreloaders = useRef(new Map());
  const uid = useId().replace(/:/g, "");
  const panelPrefix = `hero-product-story-${uid}`;
  const scene = HERO_PRODUCT_SCENES[activeIndex];
  // 2026-08-22 deep-review: the carousel wraps in every direction, so there is no meaningful
  // "final" position — the old position-based complete/Replay state fired after ONE backward
  // step from chapter 1 and mis-announced "Story complete" to screen readers. The control is
  // Play/Pause only now.
  const autoPlayRunning = Boolean(
    active
    && autoPlayRequested
    && !focusPauseLocked
    && !motionBlocked
    && documentVisible
  );

  const clearAutoPlayTimer = useCallback(() => {
    if (autoPlayTimerRef.current === null) return;
    window.clearTimeout(autoPlayTimerRef.current);
    autoPlayTimerRef.current = null;
  }, []);

  const warmScene = useCallback((index) => {
    if (typeof Image === "undefined") return;
    const nextScene = HERO_PRODUCT_SCENES[index];
    if (!nextScene || scenePreloaders.current.has(nextScene.image)) return;
    const preloader = new Image();
    preloader.decoding = "async";
    preloader.fetchPriority = "low";
    preloader.onerror = () => scenePreloaders.current.delete(nextScene.image);
    // PERF-5/VIS-2: mirror the visible <img>'s srcset/sizes BEFORE assigning src so the browser's
    // candidate selection warms the SAME variant it will render. The old `src = master` preloaded
    // the 812–995KB lossless master per scene (~4MB across the 5-scene autoplay) that the visible
    // img then never displayed. srcset/sizes must be set first — assigning src alone triggers an
    // immediate fetch of the full-resolution file.
    // PERF-9: the visible node is a <picture> offering AVIF first, and a bare Image() does no type
    // negotiation — warming the WebP rung would cost a SECOND download rather than save one.
    attachAvifSource(preloader, productStoryAvifSrcSetFor(nextScene.image, nextScene.imageWidth), HERO_IMAGE_SIZES);
    preloader.sizes = HERO_IMAGE_SIZES;
    preloader.srcset = productStorySrcSetFor(nextScene.image, nextScene.imageWidth);
    preloader.src = nextScene.image;
    scenePreloaders.current.set(nextScene.image, preloader);
    preloader.decode?.().catch(() => {
      // Warming is opportunistic; the visible product image remains the source of truth.
    });
  }, []);

  // The story is a CAROUSEL at its edges: previous from the first chapter lands on the last, next
  // from the last lands on the first — the same wrap the tab-list arrow keys always had, so the
  // visible buttons can never dead-end (the old finite-edge design disabled them there, which read
  // as "the buttons don't work"). `direction` is the direction of TRAVEL (+1 forward / -1 back) and
  // drives the chapter-turn motion; callers that know it pass it, tab jumps derive it from order.
  const selectStory = (index, source = "pointer", direction) => {
    const count = HERO_PRODUCT_SCENES.length;
    const next = ((index % count) + count) % count;
    clearAutoPlayTimer();
    setInputSource(source);
    setTravelDirection(direction ?? (next >= activeIndex ? 1 : -1));
    if (source !== "autoplay") setFocusPauseLocked(true);
    setActiveIndex(next);
    setClockToken((token) => token + 1);
  };

  useEffect(() => {
    const onVisibilityChange = () => {
      const visible = document.visibilityState !== "hidden";
      if (!visible) clearAutoPlayTimer();
      setDocumentVisible(visible);
      if (visible) setClockToken((token) => token + 1);
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [clearAutoPlayTimer]);

  useEffect(() => {
    clearAutoPlayTimer();
    if (!autoPlayRunning) return undefined;
    autoPlayTimerRef.current = window.setTimeout(() => {
      autoPlayTimerRef.current = null;
      setInputSource("autoplay");
      setTravelDirection(1);
      setActiveIndex((index) => (index + 1) % HERO_PRODUCT_SCENES.length);
      setClockToken((token) => token + 1);
    }, HERO_STORY_INTERVAL_MS);
    return clearAutoPlayTimer;
  }, [activeIndex, autoPlayRunning, clearAutoPlayTimer, clockToken]);

  useEffect(() => {
    if (!active || motionBlocked || !autoPlayRequested || focusPauseLocked || typeof Image === "undefined") return undefined;
    const timer = window.setTimeout(() => {
      warmScene((activeIndex + 1) % HERO_PRODUCT_SCENES.length);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [active, activeIndex, autoPlayRequested, focusPauseLocked, motionBlocked, warmScene]);

  useEffect(() => () => {
    scenePreloaders.current.forEach((preloader) => {
      preloader.onload = null;
      preloader.onerror = null;
    });
    scenePreloaders.current.clear();
  }, []);

  const onTabKeyDown = (event, index) => {
    let next = index;
    let direction;
    if (event.key === "ArrowRight" || event.key === "ArrowDown") { next = index + 1; direction = 1; }
    else if (event.key === "ArrowLeft" || event.key === "ArrowUp") { next = index - 1; direction = -1; }
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = HERO_PRODUCT_SCENES.length - 1;
    else return;
    event.preventDefault();
    selectStory(next, "keyboard", direction);
    tabRefs.current[((next % HERO_PRODUCT_SCENES.length) + HERO_PRODUCT_SCENES.length) % HERO_PRODUCT_SCENES.length]?.focus();
  };

  const toggleAutoPlay = (event) => {
    if (motionBlocked) return;
    const control = event.currentTarget;
    rotationControlPointerDown.current = false;
    clearAutoPlayTimer();
    if (autoPlayRequested && !focusPauseLocked) {
      setAutoPlayRequested(false);
    } else {
      setAutoPlayRequested(true);
      setFocusPauseLocked(false);
    }
    setClockToken((token) => token + 1);
    if (event.detail > 0) window.requestAnimationFrame(() => control.blur());
  };

  const swipeHandlers = {
    onPointerDown: (event) => {
      if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0)) return;
      swipeStart.current = { id: event.pointerId, x: event.clientX, y: event.clientY, time: Date.now() };
    },
    onPointerUp: (event) => {
      const start = swipeStart.current;
      swipeStart.current = null;
      if (!start || start.id !== event.pointerId) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      const elapsed = Date.now() - start.time;
      if (elapsed > 800 || Math.abs(dx) < 48 || Math.abs(dx) <= Math.abs(dy) * 1.25) return;
      selectStory(activeIndex + (dx < 0 ? 1 : -1), "pointer", dx < 0 ? 1 : -1);
    },
    onPointerCancel: () => { swipeStart.current = null; },
  };

  const autoPlayState = motionBlocked
    ? "motion-paused"
    : !autoPlayRequested || focusPauseLocked
      ? "paused"
      : !active
        ? "out-of-view"
        : !documentVisible
          ? "document-hidden"
          : "playing";

  const rotationAction = motionBlocked
    ? "Motion paused"
    : autoPlayRequested && !focusPauseLocked
      ? "Pause automatic product story"
      : "Play automatic product story";

  // Announced to assistive tech only — the visible timing chip ("Playing · 3 seconds per chapter")
  // was removed by product decision 2026-08-21: the tab progress bar already shows the cadence.
  const status = motionBlocked
    ? "Motion paused"
    : !active
      ? "Waiting until visible"
      : !autoPlayRequested || focusPauseLocked
        ? "Manual control"
        : !documentVisible
          ? "Paused while this tab is hidden"
            : "Playing";

  const handleFocusCapture = (event) => {
    const isRotationControl = event.target?.dataset?.testid === "hero-story-autoplay-toggle";
    if (isRotationControl && rotationControlPointerDown.current) return;
    if (autoPlayRequested) {
      clearAutoPlayTimer();
      setFocusPauseLocked(true);
      setInputSource("keyboard");
    }
  };

  return (
    <figure
      className={cn(
        "hero-dash relative isolate flex h-auto min-h-0 w-full flex-col overflow-hidden rounded-[24px] border border-line bg-surface text-left text-ink",
        "shadow-[0_38px_100px_-42px_rgba(2,12,35,0.72)] lg:h-full",
      )}
      data-testid="illustrative-school-operations"
      data-conceptual="true"
      data-rendering="governed-product-visuals"
      data-theme-policy="surrounding-chrome"
      data-autoplay-state={autoPlayState}
      aria-labelledby={`${panelPrefix}-title`}
      onFocusCapture={handleFocusCapture}
    >
      <div
        className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-64 opacity-80"
        style={{ background: "radial-gradient(circle at 68% 0%, rgb(var(--vp-accent) / 0.14), transparent 62%)" }}
        aria-hidden="true"
      />

      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line bg-surface/95 px-4 py-3 sm:px-5">
        <figcaption className="min-w-0">
          <p className="text-[9px] font-bold uppercase tracking-[0.17em] text-accent">Guided product view</p>
          <p id={`${panelPrefix}-title`} className="mt-0.5 text-[13px] font-bold leading-tight text-ink sm:text-[14px]">
            Five connected moments. One school operating context.
          </p>
        </figcaption>

        <div className="flex min-w-0 flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={toggleAutoPlay}
            aria-label={rotationAction}
            aria-disabled={motionBlocked}
            onPointerDown={() => { rotationControlPointerDown.current = true; }}
            onPointerCancel={() => { rotationControlPointerDown.current = false; }}
            data-testid="hero-story-autoplay-toggle"
            className="inline-flex min-h-11 items-center gap-2 rounded-full border border-line bg-canvas px-3 text-[10px] font-semibold text-ink-subtle transition-[background-color,border-color,color,transform] duration-150 ease-premium hover:border-line-strong hover:text-ink active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/65 aria-disabled:cursor-not-allowed aria-disabled:opacity-60"
          >
            {autoPlayRequested && !focusPauseLocked && !motionBlocked
              ? <Pause className="h-3.5 w-3.5" aria-hidden="true" />
              : <Play className="h-3.5 w-3.5" aria-hidden="true" />}
            {autoPlayRequested && !focusPauseLocked && !motionBlocked ? "Pause" : "Play"}
          </button>
          <span className="sr-only" aria-live="polite">{status}</span>
          <div className="flex items-center rounded-full border border-line bg-canvas p-0.5" role="group" aria-label="Product story controls">
            <button
              type="button"
              onClick={(event) => selectStory(activeIndex - 1, event.detail === 0 ? "keyboard" : "pointer", -1)}
              aria-label="Previous product chapter"
              data-testid="hero-story-previous"
              className="grid h-10 w-10 place-items-center rounded-full text-ink-subtle transition-[background-color,color,transform] duration-150 ease-premium hover:bg-muted hover:text-ink active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/65"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={(event) => selectStory(activeIndex + 1, event.detail === 0 ? "keyboard" : "pointer", 1)}
              aria-label="Next product chapter"
              data-testid="hero-story-next"
              className="grid h-10 w-10 place-items-center rounded-full text-ink-subtle transition-[background-color,color,transform] duration-150 ease-premium hover:bg-muted hover:text-ink active:scale-[0.96] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focusring/65"
            >
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </header>

      <StoryTabs
        activeIndex={activeIndex}
        panelPrefix={panelPrefix}
        onSelect={selectStory}
        onKeyDown={onTabKeyDown}
        onWarm={warmScene}
        refs={tabRefs}
        running={autoPlayRunning}
        clockToken={clockToken}
      />

      <StoryPanel
        scene={scene}
        index={activeIndex}
        panelPrefix={panelPrefix}
        liveMode={autoPlayRunning ? "off" : "polite"}
        instant={motionBlocked || inputSource === "keyboard"}
        onSwipe={swipeHandlers}
        direction={travelDirection}
      />

      <footer className="flex flex-wrap items-center justify-between gap-x-5 gap-y-1.5 border-t border-line bg-surface/95 px-4 py-2.5 sm:px-5">
        <p className="inline-flex items-center gap-2 text-[9.5px] leading-relaxed text-ink-subtle">
          <CircleDot className="h-3.5 w-3.5 shrink-0 text-accent" aria-hidden="true" />
          Illustrative product preview · synthetic sample data · not a live customer screen
        </p>
        <p className="inline-flex items-center gap-1.5 text-[9.5px] font-semibold leading-relaxed text-ink-subtle">
          <Check className="h-3.5 w-3.5 shrink-0 text-positive" aria-hidden="true" />
          {scene.availability}
        </p>
      </footer>
    </figure>
  );
}
